'use client';

import { createContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FullApiClient, AuthUser } from '@heyroot/api-client';

import {
  initializeFirebase,
  signInWithGoogle,
  firebaseSignOutAndClear,
  type FirebaseOptions,
} from './firebase.js';
import { setCurrentCsrfToken } from './csrf.js';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  signIn: (params: { tenantId?: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  firebaseConfig: FirebaseOptions;
  apiClient: FullApiClient;
  children: ReactNode;
}

export function AuthProvider({ firebaseConfig, apiClient, children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  // Capture firebaseConfig + apiClient at MOUNT TIME via a ref so the bootstrap
  // effect runs exactly once even if the parent passes new object identities on
  // re-render. This provider is intended to mount once at the app root with
  // stable refs; if a future caller really needs to swap configs at runtime,
  // unmount + remount the provider rather than mutating these props.
  const initRef = useRef({ firebaseConfig, apiClient });

  useEffect(() => {
    let cancelled = false;
    const { firebaseConfig: cfg, apiClient: client } = initRef.current;

    initializeFirebase(cfg);

    // Hard-reload bootstrap: try /auth/refresh.
    // The cancelled guard prevents:
    //   (a) React 18 StrictMode double-fire from double-applying state, and
    //   (b) a stale promise from overwriting state if the provider unmounts
    //       before /auth/refresh resolves.
    client.auth
      .refresh()
      .then((response) => {
        if (cancelled) return;
        setUser(response.user);
        setCurrentCsrfToken(response.csrfToken);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setCurrentCsrfToken(null);
        setStatus('unauthenticated');
      });

    return () => {
      cancelled = true;
    };
    // The effect intentionally captures props at mount via initRef and runs
    // exactly once. Adding [firebaseConfig, apiClient] to the dep array would
    // re-fire /auth/refresh on every parent re-render (object-identity change),
    // thrashing the CSRF token mid-session. The provider is documented as
    // mount-once-with-stable-refs; runtime config swap requires unmount+remount.
  }, []);

  async function signIn({ tenantId }: { tenantId?: string }) {
    const { idToken } = await signInWithGoogle();
    const response = await apiClient.auth.login({
      idToken,
      ...(tenantId ? { tenantId } : {}),
    });
    setUser(response.user);
    setCurrentCsrfToken(response.csrfToken);
    setStatus('authenticated');
  }

  async function signOut() {
    try {
      await apiClient.auth.logout();
    } finally {
      await firebaseSignOutAndClear();
      setUser(null);
      setCurrentCsrfToken(null);
      setStatus('unauthenticated');
    }
  }

  return (
    <AuthContext.Provider value={{ status, user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
